/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import Ember from 'ember';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { isEmpty, isNone } from '@ember/utils';
import { task } from 'ember-concurrency';
import { timeout } from 'ember-concurrency';
import { singularize } from 'ember-inflector';
import moment from 'moment-timezone';

import { seriesString } from 'cio/utils/formatters';
import pluralize from 'cio/utils/pluralize';
import { LOCAL_TIME_ZONE } from 'cio/utils/time-zones';
import { padNumber } from 'cio/utils/formatters';

import AnalyticsService from 'cio/services/analytics';
import NewsletterModel from 'cio/models/newsletter';

export type TimeZoneOptionType = 'specific' | 'user';

// Disable newsletter rescheduling after time remaining falls below this number of minutes
const SCHEDULING_THRESHOLD = 5;

const roundTimestamp = (timestamp: number) => {
  let momentTimestamp = moment.unix(timestamp);
  let hour = momentTimestamp.hour();
  let minute = momentTimestamp.minute();

  // Round up to the next 15 minutes to accommodate time value choices
  minute = Math.ceil(minute / 15) * 15;
  if (minute === 60) {
    if (hour === 23) {
      // Round everything between 11:46pm and 11:59pm to 11:59pm
      minute = 59;
    } else {
      // Round everything after 45 minutes to the next hour (if it's not
      // after 11:45pm)
      hour++;
      minute = 0;
    }
  }

  return momentTimestamp.hour(hour).minute(minute).unix();
};

interface CioScheduleEditorArgs {
  allowUserTimeZoneSelection: boolean;
  duration: moment.Duration;
  durationHumanReadable: string;
  hasTestSetup: boolean;
  newsletter: NewsletterModel;
  tzMatchEnabled: boolean;
  timestamp: number;
  timezone: string;
  onCancel: () => unknown;
  onSchedule: (schedulingOptions: { timezone: string; timestamp: number; tzMatchEnabled: boolean }) => void;
  disabled: boolean;
}

export default class CioScheduleEditor extends Component<CioScheduleEditorArgs> {
  @service analytics!: AnalyticsService;

  newsletter!: NewsletterModel;

  @tracked isScheduling = false;
  @tracked daySelected: number;
  @tracked monthSelected: number;
  @tracked yearSelected: number;
  @tracked currentMomentTime: moment.Moment;
  @tracked formattedMomentTime: string;
  @tracked timeZoneSelected: string;
  @tracked sendTimeZoneOption: TimeZoneOptionType;

  get selectedMomentTime() {
    let { yearSelected: year, monthSelected, daySelected, formattedMomentTime, timeZoneSelected: timezone } = this;

    let [hourSelected, minuteSelected] = formattedMomentTime.split(':');

    let month = padNumber(monthSelected + 1);
    let day = padNumber(daySelected);
    let hour = padNumber(hourSelected);
    let minute = padNumber(minuteSelected);

    return moment.tz(`${year}-${month}-${day} ${hour}:${minute}:00`, timezone);
  }

  get isPast() {
    return !this.selectedMomentTime.isAfter(this.currentMomentTime);
  }

  get invalidSchedule() {
    return this.isPast || this.isScheduling;
  }

  get formattedTimeRemaining() {
    if (this.isPast) {
      return null;
    }

    let { currentMomentTime, selectedMomentTime } = this;

    // moment-precise-range doesn't handle different timezones very well. When
    // changing one moment's time to another's timezone, it just adds minutes
    // based on utcOffsets, but doesn't change the utcOffsets themselves. Later
    // it calls moment's isAfter method, which does take into account
    // utcOffsets, resulting in wrong time differences.
    currentMomentTime = currentMomentTime.clone().tz('UTC');
    selectedMomentTime = selectedMomentTime.clone().tz('UTC');

    let rawTimeRemaining = selectedMomentTime.preciseDiff(currentMomentTime);
    if (rawTimeRemaining === '') {
      return null;
    }

    let timeRemaining = selectedMomentTime.preciseDiff(currentMomentTime, true);

    let units = [];
    if (timeRemaining.years || timeRemaining.months) {
      // Show years, months, and days if there is at least 1 month remaining
      units = ['years', 'months', 'days'];
    } else if (timeRemaining.days || timeRemaining.hours || timeRemaining.minutes) {
      // Otherwise, show days, hours, and minutes if there is at least 1 minute
      // remaining
      units = ['days', 'hours', 'minutes'];
    } else {
      return 'Less than a minute';
    }

    let remainingSeries: string[] = units.reduce(
      (series: string[], unit: 'years' | 'months' | 'days' | 'hours' | 'minutes') => {
        let unitRemaining = timeRemaining[unit];

        if (unitRemaining) {
          // Add one minute for rounding up, since we're not showing seconds
          if (unit === 'minutes' && timeRemaining.seconds > 0) {
            unitRemaining = unitRemaining + 1;
          }
          let unitLabel = pluralize(unitRemaining, singularize(unit), unit);
          return series.concat(`${unitRemaining} ${unitLabel}`);
        } else {
          return series;
        }
      },
      [],
    );

    // Expected format for `formattedTimeRemaining`: "1 year, 7 months and 27
    // days"
    return seriesString(remainingSeries.join());
  }

  tick = task({ drop: true }, async () => {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      // Update current time every full minute
      let secondsToFullMinute = 60 - new Date().getSeconds();
      await timeout(secondsToFullMinute * 1000);
      this.currentMomentTime = moment();
    }
  });

  constructor(owner: unknown, args: CioScheduleEditorArgs) {
    super(owner, args);

    this.newsletter = args.newsletter;

    this.currentMomentTime = moment();
    if (!Ember.testing) {
      this.tick.perform();
    }

    let { timestamp, timezone } = args;

    // If the timestamp is undefined, use the current time
    // advanced forward by the number of minutes in the scheduling threshold
    // and rounded to the next minute
    if (isNone(timestamp) || timestamp === 0) {
      timestamp = moment()
        .second(0)
        .add(SCHEDULING_THRESHOLD + 1, 'm')
        .unix();
    }
    if (isEmpty(timezone)) {
      // fallback to local timezone detected during app initialization
      timezone = LOCAL_TIME_ZONE.value;
    }

    let momentTime = moment.unix(roundTimestamp(timestamp)).tz(timezone);

    this.daySelected = momentTime.date();
    this.monthSelected = momentTime.month();
    this.yearSelected = momentTime.year();
    this.formattedMomentTime = momentTime.format('H:mm');
    this.timeZoneSelected = timezone;
    this.sendTimeZoneOption = !args.duration && !args.hasTestSetup && args.tzMatchEnabled ? 'user' : 'specific';
  }

  @action
  onScheduleAction() {
    this.isScheduling = true;

    this.args.onSchedule({
      timezone: this.timeZoneSelected,
      timestamp: this.selectedMomentTime.unix(),
      tzMatchEnabled: this.sendTimeZoneOption === 'user',
    });
  }
  @action
  didSelectDate(momentDate: moment.Moment) {
    this.daySelected = momentDate.date();
    this.monthSelected = momentDate.month();
    this.yearSelected = momentDate.year();
  }
  @action
  setTimeZoneOption(value: TimeZoneOptionType) {
    this.sendTimeZoneOption = value;
    this.analytics.track('broadcast', 'edit_started', { model: this.newsletter, editType: value });
  }
}
